import * as R from 'ramda';
import { put, call, fork, select, takeLatest } from 'redux-saga/effects';
// components
import { openLoader, closeLoader } from '../../../../components/loader/actions';
// features
import { makeSelectCurrentBranchGuid } from '../../../branch/selectors';
// helpers/constants
import * as G from '../../../../helpers';
import * as GC from '../../../../constants';
// sagas
import { crudSaga, visitPageSaga } from '../../../../sagas';
// utilities
import endpointsMap from '../../../../utilities/endpoints';
// feature configs
import * as A from '../../actions';
import { getConfigsForCloConfigSuccess, getAccessorialConfigListForCloConfigSuccess } from './actions';
import {
  getConfigByGroupSaga,
  getSharedAccessorialListSaga,
  getStatusMessagesForConfigSaga,
  getSequencesByTypeForConfigSaga,
  getTermsAndConditionsDocumentRequest,
  getReferenceTypesByScopeForConfigSaga,
} from '../../sagas';
//////////////////////////////////////////////////

function* getAccessorialConfigListForCloConfigRequest() {
  try {
    const branchGuid = yield select(makeSelectCurrentBranchGuid());
    const options = {
      params: {
        [GC.FIELD_BRANCH_GUID]: branchGuid,
      },
    };
    yield call(crudSaga, {
      options,
      method: 'get',
      callbackSaga: getSharedAccessorialListSaga,
      endpoint: endpointsMap.accessorialsListEndpoint,
      successAction: getAccessorialConfigListForCloConfigSuccess,
      parentSagaName: 'getAccessorialConfigListForCloConfigRequest',
    });
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown exception');
    yield call(G.handleException, error, 'getAccessorialConfigListForCloConfigRequest fail');
  }
}

function* getConfigsForCloConfigSaga() {
  try {
    const names = R.join(',', [
      GC.GENERAL_EQUIPMENTS,
      GC.TEMPLATES_LOCATION_TYPE,
      GC.CLO_ORDER_TYPE_DROPDOWN,
      GC.CLO_ITEM_DAMAGE_REPORT_AREA,
      GC.COMMUNICATION_DOCUMENT_TYPE,
      GC.GENERAL_MODE_TRANSPORTATION,
      GC.GENERAL_TRANSPORTATION_SERVICE_TYPE,
    ]);

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      params: {
        names,
        [GC.FIELD_BRANCH_GUID]: branchGuid,
      },
    };

    yield call(crudSaga, {
      options,
      method: 'get',
      shouldCloseModal: true,
      mapper: G.mapConfigValuesByName,
      endpoint: endpointsMap.branchConfigsEndpoint,
      parentSagaName: 'getConfigsForCloConfigSaga',
      successAction: getConfigsForCloConfigSuccess,
    });
  } catch (error) {
    yield call(G.handleException, error, 'getConfigsForCloConfigSaga exception');
  }
}

// default order accessorials
function* getDefaultOrderAccessorialListRequest() {
  try {
    const branchGuid = yield select(makeSelectCurrentBranchGuid());
    const options = {
      params: {
        [GC.FIELD_BRANCH_GUID]: branchGuid,
      },
    };
    yield call(crudSaga, {
      options,
      method: 'get',
      endpoint: endpointsMap.defaultOrderAccessorialList,
      successAction: A.getDefaultOrderAccessorialListSuccess,
      parentSagaName: 'getDefaultOrderAccessorialListRequest',
    });
  } catch (error) {
    yield call(G.handleException, error, 'getDefaultOrderAccessorialListRequest exception');
  }
}

function* createOrUpdateDefaultOrderAccessorialRequest({ payload }) {
  try {
    yield put(openLoader());
    const branchGuid = yield select(makeSelectCurrentBranchGuid());
    const method = G.ifElse(
      G.isNotNilAndNotEmpty(payload.version),
      'put',
      'post',
    );
    const options = {
      data: R.assoc(GC.FIELD_BRANCH_GUID, branchGuid, payload),
    };
    yield call(crudSaga, {
      method,
      options,
      shouldCloseModal: true,
      showSuccessMessage: true,
      successMessage: 'messages:success:200-201',
      endpoint: endpointsMap.defaultOrderAccessorial,
      successAction: A.createOrUpdateDefaultOrderAccessorialSuccess,
      parentSagaName: 'createOrUpdateDefaultOrderAccessorialRequest',
    });
    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());
    yield call(G.handleException, error, 'createOrUpdateServiceTypeMappingRequest exception');
  }
}

function* removeDefaultOrderAccessorialRequest({ payload }) {
  try {
    yield put(openLoader());
    const branchGuid = yield select(makeSelectCurrentBranchGuid());
    const options = {
      data: {
        [GC.FIELD_GUID]: payload,
        [GC.BRANCH_GUID]: branchGuid,
      },
    };
    yield call(crudSaga, {
      options,
      payload,
      method: 'delete',
      showSuccessMessage: true,
      successMessage: 'messages:success:204',
      endpoint: endpointsMap.defaultOrderAccessorial,
      successAction: A.removeDefaultOrderAccessorialSuccess,
      parentSagaName: 'removeDefaultOrderAccessorialRequest',
    });
    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());
    yield call(G.handleException, error, 'removeDefaultOrderAccessorialRequest exception');
  }
}

// order type mapping
function* getOrderTypeMappingListByTypeRequest({ payload }) {
  try {
    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const mappingType = R.or(payload, 'inboundMapping');

    const options = {
      params: {
        [GC.FIELD_BRANCH_GUID]: branchGuid,
      },
    };

    const mapper = (data) => ({ data, mappingType });

    yield call(crudSaga, {
      mapper,
      options,
      method: 'get',
      successAction: A.getOrderTypeMappingListByTypeSuccess,
      parentSagaName: 'getOrderTypeMappingListByTypeRequest',
      endpoint: endpointsMap.orderTypeMappingListByType(mappingType),
    });
  } catch (error) {
    yield call(G.handleException, error, 'getOrderTypeMappingListByTypeRequest exception');
  }
}

function* createOrUpdateOrderTypeMappingByTypeRequest({ payload }) {
  try {
    const { values, mappingType } = payload;

    yield put(openLoader());

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const method = G.ifElse(
      G.isNotNilAndNotEmpty(values.version),
      'put',
      'post',
    );

    const options = {
      data: R.assoc(GC.FIELD_BRANCH_GUID, branchGuid, values),
    };

    const mapper = (data) => ({ data, mappingType });

    yield call(crudSaga, {
      mapper,
      method,
      options,
      shouldCloseModal: true,
      showSuccessMessage: true,
      successMessage: 'messages:success:200-201',
      endpoint: endpointsMap.orderTypeMappingByType(mappingType),
      successAction: A.createOrUpdateOrderTypeMappingByTypeSuccess,
      parentSagaName: 'createOrUpdateOrderTypeMappingByTypeRequest',
    });
  } catch (error) {
    yield call(G.handleException, error, 'createOrUpdateOrderTypeMappingByTypeRequest exception');
  } finally {
    yield put(closeLoader());
  }
}

function* removeOrderTypeMappingByTypeRequest({ payload }) {
  try {
    yield put(openLoader());

    const { guid, mappingType } = payload;

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      data: {
        guid,
        [GC.BRANCH_GUID]: branchGuid,
      },
    };

    const mapper = () => ({ guid, mappingType });

    yield call(crudSaga, {
      mapper,
      options,
      payload,
      method: 'delete',
      showSuccessMessage: true,
      successMessage: 'messages:success:204',
      successAction: A.removeOrderTypeMappingByTypeSuccess,
      parentSagaName: 'removeOrderTypeMappingByTypeRequest',
      endpoint: endpointsMap.orderTypeMappingByType(mappingType),
    });
  } catch (error) {
    yield call(G.handleException, error, 'removeOrderTypeMappingByTypeRequest exception');
  } finally {
    yield put(closeLoader());
  }
}

function* handleVisitConfigCloPageSaga({ payload }) {
  while (true) { // eslint-disable-line
    yield call(visitPageSaga, payload, GC.CHECK_VISIT_CONFIG_CLO_PAGE);

    yield put(openLoader({ showDimmer: true }));

    yield call(getConfigByGroupSaga, GC.CLO_CONFIG_GROUP);
    yield call(getStatusMessagesForConfigSaga);
    yield call(getAccessorialConfigListForCloConfigRequest);
    yield call(getSequencesByTypeForConfigSaga, { payload: GC.SEQUENCE_TYPE_CLO });
    yield call(getReferenceTypesByScopeForConfigSaga, { payload: GC.REF_SCOPE_NAME_TEL });
    yield call(getReferenceTypesByScopeForConfigSaga, { payload: GC.REF_SCOPE_NAME_CLO });
    yield call(getDefaultOrderAccessorialListRequest);
    yield call(getTermsAndConditionsDocumentRequest, { payload: GC.CLO_CONFIG_GROUP });
    yield call(getOrderTypeMappingListByTypeRequest, { payload: 'inboundMapping' });

    yield fork(getConfigsForCloConfigSaga);

    yield put(A.getAvailableDocumentTypes());
    yield put(closeLoader());

    break;
  }
}

function* cloConfigWatcherSaga() {
  yield takeLatest(GC.VISIT_CONFIG_CLO_PAGE, handleVisitConfigCloPageSaga);
  // default order accessorials
  yield takeLatest(A.removeDefaultOrderAccessorialRequest, removeDefaultOrderAccessorialRequest);
  yield takeLatest(A.getDefaultOrderAccessorialListRequest, getDefaultOrderAccessorialListRequest);
  yield takeLatest(A.createOrUpdateDefaultOrderAccessorialRequest, createOrUpdateDefaultOrderAccessorialRequest);
  // order type mapping
  yield takeLatest(A.removeOrderTypeMappingByTypeRequest, removeOrderTypeMappingByTypeRequest);
  yield takeLatest(A.getOrderTypeMappingListByTypeRequest, getOrderTypeMappingListByTypeRequest);
  yield takeLatest(A.createOrUpdateOrderTypeMappingByTypeRequest, createOrUpdateOrderTypeMappingByTypeRequest);
}

export default cloConfigWatcherSaga;
